import React, { useState } from 'react';
import { Play, Square, Coffee, Clock, Pause } from 'lucide-react';
import { fichajeAPI, textoErrorApi } from '../../api/api';
import { useAvisoTemporal, AvisoInline } from './RegistroHorarioEmpleado';
import { AVISO_SIMULACION } from '../../context/VistaEmpleadoContext';

// ==================== FICHAJE RÁPIDO ====================
// Botones de fichar (entrada, pausa, reanudar, salida) según el estado del
// día. Lo usan Mi Inicio (pestaña Hoy) y la página de Fichaje. Soporta
// jornadas partidas: con la jornada cerrada se puede volver a fichar entrada.
//
// Estados: 'sin_fichar' | 'trabajando' | 'en_pausa' | 'fuera_de_jornada'
// (Mi Inicio, servidor) o 'terminado' (Fichaje.js, deducido del registro).

// Estado a partir de un registro de `registros_horarios` (el abierto de hoy o
// el último cerrado)
export const estadoFichaje = (registro) => {
  if (!registro?.hora_entrada) return 'sin_fichar';
  if (registro.hora_salida) return 'terminado';
  if (registro.en_pausa) return 'en_pausa';
  return 'trabajando';
};

// Hora corta de un fichaje. El servidor guarda en UTC; si llega sin zona se
// toma como UTC. Si ya viene como "HH:MM" se pinta tal cual.
export const horaFichaje = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(texto)) return texto.slice(0, 5);
  const conZona = /([zZ]|[+-]\d{2}:?\d{2})$/.test(texto) ? texto : `${texto}Z`;
  const d = new Date(conZona);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

const ESTADOS = {
  sin_fichar: { texto: 'Sin fichar hoy', clases: 'bg-gray-100 text-gray-700', Icono: Clock },
  trabajando: { texto: 'Trabajando', clases: 'bg-green-100 text-green-800', Icono: Play },
  en_pausa: { texto: 'En pausa', clases: 'bg-amber-100 text-amber-800', Icono: Pause },
  terminado: { texto: 'Jornada cerrada', clases: 'bg-blue-100 text-blue-800', Icono: Coffee },
  fuera_de_jornada: { texto: 'Fuera de jornada', clases: 'bg-blue-100 text-blue-800', Icono: Coffee },
};

const ACCIONES = {
  entrada: { llamada: fichajeAPI.entrada, ok: 'Entrada registrada', fallo: 'No se pudo registrar la entrada' },
  salida: { llamada: fichajeAPI.salida, ok: 'Salida registrada', fallo: 'No se pudo registrar la salida' },
  pausa: { llamada: fichajeAPI.iniciarPausa, ok: 'Pausa iniciada', fallo: 'No se pudo iniciar la pausa' },
  reanudar: { llamada: fichajeAPI.terminarPausa, ok: 'Pausa terminada', fallo: 'No se pudo terminar la pausa' },
};

/**
 * - empleadoId: id del empleado que ficha.
 * - estado: uno de los estados de arriba.
 * - horaEntrada, horaSalida, pausaDesde, minutosHoy: para el texto del estado (opcionales).
 * - permisos: { entrar, pausar, reanudar, salir } del servidor (Mi Inicio). Si
 *   llega, manda sobre lo que se deduce del estado.
 * - onFichado: se llama (y se espera) tras cada fichaje correcto, para recargar.
 * - soloLectura: "Simular vista empleado"; los botones se ven pero no fichan (aviso).
 */
// Qué botones se pueden usar en cada estado (cuando el servidor no lo dice).
// En pausa también se puede fichar la salida: el servidor cierra la pausa.
const permisosDeEstado = (estado) => ({
  entrar: estado === 'sin_fichar' || estado === 'terminado' || estado === 'fuera_de_jornada',
  pausar: estado === 'trabajando',
  reanudar: estado === 'en_pausa',
  salir: estado === 'trabajando' || estado === 'en_pausa',
});

const textoMinutos = (min) => {
  const n = Number(min);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `${Math.floor(n / 60)}h ${Math.round(n % 60)}m`;
};

const FichajeRapido = ({
  empleadoId, estado = 'sin_fichar', horaEntrada, horaSalida, pausaDesde, minutosHoy, permisos = null, onFichado,
  soloLectura = false,
}) => {
  const [enCurso, setEnCurso] = useState(null);
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal(6000);

  const ejecutar = async (clave) => {
    if (soloLectura) {
      mostrarAviso('error', AVISO_SIMULACION);
      return;
    }
    if (!empleadoId || enCurso) return;
    const accion = ACCIONES[clave];
    setEnCurso(clave);
    limpiarAviso();
    try {
      const { data } = await accion.llamada(empleadoId);
      let texto = accion.ok;
      if (clave === 'reanudar' && Number.isFinite(Number(data?.duracion_minutos))) {
        texto = `${accion.ok} (${data.duracion_minutos} min)`;
      } else if (clave === 'salida' && data?.pausa_cerrada) {
        texto = `${accion.ok} a las ${new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} (pausa cerrada)`;
      } else {
        texto = `${accion.ok} a las ${new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
      }
      if (onFichado) await onFichado();
      mostrarAviso('ok', texto);
    } catch (error) {
      mostrarAviso('error', `${accion.fallo}: ${textoErrorApi(error.response?.data?.detail, error.message || 'Error desconocido')}`);
    } finally {
      setEnCurso(null);
    }
  };

  const info = ESTADOS[estado] || ESTADOS.sin_fichar;
  const { Icono } = info;
  let detalle = '';
  if (estado === 'trabajando' && horaFichaje(horaEntrada)) detalle = `desde las ${horaFichaje(horaEntrada)}`;
  if (estado === 'en_pausa' && horaFichaje(pausaDesde)) detalle = `desde las ${horaFichaje(pausaDesde)}`;
  if (estado === 'terminado' && horaFichaje(horaSalida)) detalle = `a las ${horaFichaje(horaSalida)}`;
  if (textoMinutos(minutosHoy)) detalle = `${detalle ? `${detalle} · ` : ''}hoy ${textoMinutos(minutosHoy)}`;

  const puede = permisos || permisosDeEstado(estado);
  const enJornada = estado === 'trabajando' || estado === 'en_pausa';
  const yaFichoHoy = estado === 'terminado' || !!horaSalida || Number(minutosHoy) > 0;
  const ocupado = enCurso !== null;
  const base = `w-full flex items-center justify-center gap-3 py-4 rounded-xl text-lg font-bold transition disabled:opacity-60 disabled:cursor-not-allowed${soloLectura ? ' opacity-60 cursor-not-allowed' : ''}`;
  // En solo lectura no se usa `disabled` (el clic tiene que enseñar el aviso)
  const inactivo = (condicion) => (soloLectura ? false : condicion);

  return (
    <div className="space-y-3" data-testid="fichaje-rapido">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${info.clases}`}>
          <Icono className="w-4 h-4" />
          {info.texto}
        </span>
        {detalle && <span className="text-sm text-gray-600">{detalle}</span>}
      </div>

      {puede.entrar && (
        <button
          type="button"
          onClick={() => ejecutar('entrada')}
          data-uso="fichaje.entrada"
          disabled={inactivo(ocupado || !empleadoId)}
          aria-disabled={soloLectura || undefined}
          className={`${base} bg-green-600 hover:bg-green-700 text-white`}
          data-testid="fichar-entrada"
          data-tour="fichar"
        >
          <Play className="w-6 h-6" />
          {enCurso === 'entrada' ? 'Fichando...' : (yaFichoHoy ? 'Fichar otra entrada' : 'Fichar entrada')}
        </button>
      )}

      {(enJornada || puede.pausar || puede.reanudar || puede.salir) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!puede.reanudar ? (
            <button
              type="button"
              onClick={() => ejecutar('pausa')}
              data-uso="fichaje.pausa"
              data-uso-detalle="iniciar"
              disabled={inactivo(ocupado || !puede.pausar)}
              aria-disabled={soloLectura || undefined}
              className={`${base} bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300`}
              data-testid="fichar-pausa"
            >
              <Coffee className="w-6 h-6" />
              {enCurso === 'pausa' ? 'Un momento...' : 'Pausa'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => ejecutar('reanudar')}
              data-uso="fichaje.pausa"
              data-uso-detalle="terminar"
              disabled={inactivo(ocupado)}
              aria-disabled={soloLectura || undefined}
              className={`${base} bg-amber-500 hover:bg-amber-600 text-white`}
              data-testid="fichar-pausa"
            >
              <Play className="w-6 h-6" />
              {enCurso === 'reanudar' ? 'Un momento...' : 'Reanudar'}
            </button>
          )}
          <button
            type="button"
            onClick={() => ejecutar('salida')}
            data-uso="fichaje.salida"
            disabled={inactivo(ocupado || !puede.salir)}
            aria-disabled={soloLectura || undefined}
            className={`${base} bg-red-600 hover:bg-red-700 text-white`}
            data-testid="fichar-salida"
            data-tour="fichar"
          >
            <Square className="w-6 h-6" />
            {enCurso === 'salida' ? 'Fichando...' : 'Fichar salida'}
          </button>
        </div>
      )}
      {estado === 'en_pausa' && (
        <p className="text-xs text-gray-500">Si fichas la salida ahora, la pausa se cierra a esta hora.</p>
      )}

      <AvisoInline aviso={aviso} onCerrar={limpiarAviso} testId="fichaje-rapido-aviso" />
    </div>
  );
};

export default FichajeRapido;
